import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { io } from 'socket.io-client';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import useAuth from '../auth/feature/auth';
import { companyService, ICompanyFull, ICompanyMember, IQueueTemplate } from './service/company.service';

/* ─── tiny icons (16 × 16 stroke-2) ─── */
const PeopleIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const PaletteIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="13.5" cy="6.5" r="2.5"/><circle cx="19" cy="13.5" r="2.5"/><circle cx="6.5" cy="13.5" r="2.5"/><circle cx="13.5" cy="20" r="2.5"/><path d="M12 2a10 10 0 1 0 0 20 2 2 0 0 0 0-4H9a2 2 0 0 1 0-4h6a2 2 0 0 0 0-4 2 2 0 0 1 0-4h1"/></svg>;
const TemplateIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>;
const ToggleIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="5" width="22" height="14" rx="7" ry="7"/><circle cx="16" cy="12" r="3"/></svg>;
const QrIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/><path d="M21 14h-3v3"/><path d="M21 21h-3v-3"/></svg>;

type Tab = 'members' | 'branding' | 'templates' | 'toggles' | 'qr';

export const CompanyDashboard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const slug = user?.companySlug;
  const isCompanyAdmin = user?.companyRole === 'admin';
  const [tab, setTab] = React.useState<Tab>('members');

  /* ─── data ─── */
  const companyQuery = useQuery({
    queryKey: ['company', slug],
    queryFn: () => companyService.getCompanyFull(slug!),
    enabled: !!slug,
  });

  const membersQuery = useQuery({
    queryKey: ['company-members', slug],
    queryFn: () => companyService.getMembers(slug!),
    enabled: !!slug,
  });

  const templatesQuery = useQuery({
    queryKey: ['company-templates', slug],
    queryFn: () => companyService.getTemplates(slug!),
    enabled: !!slug && tab === 'templates',
  });

  /* ─── WebSocket ─── */
  React.useEffect(() => {
    if (!slug) return;
    const backendUrl = (
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_BACKEND_URL_DEV ||
      'http://localhost:4200'
    ).replace(/\/api\/v1\/?$/, '');

    const socket = io(backendUrl, { transports: ['websocket', 'polling'], reconnection: true });
    socket.on('connect', () => socket.emit('join-company', slug));
    socket.on('company:updated', () => queryClient.invalidateQueries({ queryKey: ['company', slug] }));
    socket.on('company:members-updated', () => {
      queryClient.invalidateQueries({ queryKey: ['company-members', slug] });
      queryClient.invalidateQueries({ queryKey: ['company', slug] });
    });
    socket.on('reconnect', () => socket.emit('join-company', slug));
    return () => { socket.emit('leave-company', slug); socket.disconnect(); };
  }, [slug]);

  const company = companyQuery.data as ICompanyFull | undefined;
  const members = (membersQuery.data ?? []) as ICompanyMember[];
  const templates = (templatesQuery.data ?? []) as IQueueTemplate[];

  if (!slug) {
    return (
      <div className="max-w-[700px] mx-auto py-[40px] px-[16px]">
        <div className="flex flex-col items-center gap-[16px] py-[60px]">
          <div className="w-[56px] h-[56px] rounded-[16px] bg-[#F0F9FF] dark:bg-[#28282B] flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          </div>
          <h2 className="font-display text-[22px] font-bold text-base-black dark:text-white">No company yet</h2>
          <p className="font-body text-[14px] text-[#818185] dark:text-[#A0A0A3] text-center max-w-[340px]">
            Your account has Enterprise access but isn't linked to a company yet. A site admin can create one and add you.
          </p>
          {user?.isAdmin && (
            <Link
              to="/admin"
              className="h-[44px] px-[24px] inline-flex items-center gap-[8px] bg-[#1D1D1E] dark:bg-[#28282B] text-white rounded-[32px] text-[14px] font-medium hover:bg-[#29292A] dark:hover:bg-[#333336] transition-all"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
              Create Company in Admin
            </Link>
          )}
        </div>
      </div>
    );
  }

  const tabs: { key: Tab; label: string; icon: React.ReactNode; adminOnly?: boolean }[] = [
    { key: 'members', label: 'Members', icon: <PeopleIcon /> },
    { key: 'branding', label: 'Branding', icon: <PaletteIcon />, adminOnly: true },
    { key: 'templates', label: 'Templates', icon: <TemplateIcon /> },
    { key: 'toggles', label: 'Features', icon: <ToggleIcon />, adminOnly: true },
    { key: 'qr', label: 'My QR', icon: <QrIcon /> },
  ];

  return (
    <div className="max-w-[700px] mx-auto py-[40px] px-[16px]">
      <Link to="/dashboard" className="inline-flex items-center gap-[4px] font-body text-[13px] font-medium text-[#818185] dark:text-[#6B6B6F] hover:text-base-black dark:hover:text-white transition-colors mb-[8px]">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        Dashboard
      </Link>
      <div className="flex items-center gap-[12px] mb-[24px]">
        {company?.logoUrl && <img src={company.logoUrl} alt="" className="w-[36px] h-[36px] rounded-[8px] object-cover" />}
        <h1 className="font-display text-[28px] font-bold text-base-black dark:text-white">{company?.name ?? 'Company'}</h1>
        <span className="text-[11px] font-semibold px-[6px] py-[1px] rounded-[4px] bg-[#F0F9FF] dark:bg-[rgba(124,58,237,0.15)] text-[#7C3AED] border border-[#C4B5FD] dark:border-[rgba(124,58,237,0.3)]">Enterprise</span>
      </div>

      {/* Tab bar */}
      <div className="flex gap-[4px] mb-[24px] overflow-x-auto pb-[2px]">
        {tabs.filter(t => !t.adminOnly || isCompanyAdmin).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-[6px] px-[14px] py-[8px] rounded-[10px] text-[13px] font-medium transition-colors whitespace-nowrap ${
              tab === t.key
                ? 'bg-[#1D1D1E] dark:bg-[#28282B] text-white'
                : 'bg-[#EFEFF1] dark:bg-[#28282B]/50 text-[#818185] dark:text-[#A0A0A3] hover:bg-[#E5E5ED] dark:hover:bg-[#333336]'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_1px_3px_rgba(0,0,0,0.07)] p-[24px]">
        {tab === 'members' && <MembersTab slug={slug} members={members} isAdmin={isCompanyAdmin} company={company} />}
        {tab === 'branding' && isCompanyAdmin && <BrandingTab slug={slug} company={company} />}
        {tab === 'templates' && <TemplatesTab slug={slug} templates={templates} isAdmin={isCompanyAdmin} />}
        {tab === 'toggles' && isCompanyAdmin && <FeatureTogglesTab slug={slug} company={company} />}
        {tab === 'qr' && <MyQrTab members={members} userId={user?.id} />}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
/*  Members Tab                                               */
/* ═══════════════════════════════════════════════════════════ */
const MembersTab = ({ slug, members, isAdmin, company }: { slug: string; members: ICompanyMember[]; isAdmin: boolean; company?: ICompanyFull }) => {
  const queryClient = useQueryClient();
  const [email, setEmail] = React.useState('');
  const [addRole, setAddRole] = React.useState('member');

  const addMutation = useMutation({
    mutationFn: () => companyService.addMember(slug, email, addRole),
    onSuccess: () => { toast.success('Member added'); setEmail(''); queryClient.invalidateQueries({ queryKey: ['company-members', slug] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to add member'),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: number) => companyService.removeMember(slug, userId),
    onSuccess: () => { toast.success('Member removed'); queryClient.invalidateQueries({ queryKey: ['company-members', slug] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to remove member'),
  });

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: string }) => companyService.updateMemberRole(slug, userId, role),
    onSuccess: () => { toast.success('Role updated'); queryClient.invalidateQueries({ queryKey: ['company-members', slug] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update role'),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-[16px]">
        <h2 className="font-body text-[16px] font-semibold text-base-black dark:text-white">Members ({members.length}{company ? `/${company.maxSeats}` : ''})</h2>
      </div>

      {isAdmin && (
        <form onSubmit={(e) => { e.preventDefault(); if (email.trim()) addMutation.mutate(); }} className="flex items-center gap-[8px] mb-[20px]">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 h-[44px] rounded-[24px] border border-[#BFBFC0] dark:border-[rgba(255,255,255,0.12)] dark:bg-[#28282B] dark:text-white dark:placeholder-[#6B6B6F] px-[16px] text-[14px] focus:outline-none focus:ring-2 focus:ring-[rgba(93,225,255,0.2)] focus:border-[#BCEBFB] dark:focus:border-[#5DE1FF]/40 dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)]"
          />
          <select
            value={addRole}
            onChange={(e) => setAddRole(e.target.value)}
            className="h-[44px] rounded-[24px] border border-[#BFBFC0] dark:border-[rgba(255,255,255,0.12)] dark:bg-[#28282B] dark:text-white px-[12px] text-[13px] focus:outline-none"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <button
            type="submit"
            disabled={!email.trim() || addMutation.isPending}
            className="h-[44px] px-[20px] bg-[#1D1D1E] dark:bg-[#28282B] text-white rounded-[32px] text-[14px] font-medium hover:bg-[#29292A] dark:hover:bg-[#333336] transition-all disabled:opacity-50"
          >
            Add
          </button>
        </form>
      )}

      <div className="space-y-[8px]">
        {members.map((m) => (
          <div key={m.id} className="flex items-center gap-[12px] p-[12px] rounded-[12px] border border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.06)]">
            <div className="w-[32px] h-[32px] rounded-full bg-[#EFEFF1] dark:bg-[#2A2A2D] flex items-center justify-center text-[13px] font-semibold text-[#818185] shrink-0">
              {(m.user.displayName ?? m.user.email)[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-body text-[14px] font-medium text-base-black dark:text-white truncate">{m.user.displayName ?? m.user.email}</p>
              {m.user.displayName && <p className="text-[12px] text-[#818185] dark:text-[#6B6B6F] truncate">{m.user.email}</p>}
            </div>
            <span className={`text-[11px] font-semibold px-[6px] py-[1px] rounded-[4px] shrink-0 ${
              m.role === 'admin' ? 'bg-[#F0F9FF] dark:bg-[rgba(124,58,237,0.15)] text-[#7C3AED] border border-[#C4B5FD] dark:border-[rgba(124,58,237,0.3)]' : 'bg-[#EFEFF1] dark:bg-[#28282B] text-[#818185] dark:text-[#A0A0A3]'
            }`}>{m.role}</span>
            {isAdmin && company && m.userId !== company.ownerId && (
              <div className="flex items-center gap-[4px] shrink-0">
                <button
                  onClick={() => roleMutation.mutate({ userId: m.userId, role: m.role === 'admin' ? 'member' : 'admin' })}
                  className="text-[11px] px-[6px] py-[2px] rounded-[6px] border border-[rgba(0,0,0,0.15)] dark:border-[rgba(255,255,255,0.08)] text-[#818185] dark:text-[#A0A0A3] hover:bg-[#EFEFF1] dark:hover:bg-[#333336] transition-colors"
                >
                  {m.role === 'admin' ? 'Demote' : 'Promote'}
                </button>
                <button
                  onClick={() => removeMutation.mutate(m.userId)}
                  className="text-[11px] px-[6px] py-[2px] rounded-[6px] border border-red-200 dark:border-red-500/30 text-[#D8210C] hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ))}
        {members.length === 0 && (
          <p className="text-center text-[14px] text-[#818185] dark:text-[#A0A0A3] py-[24px]">No members yet</p>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
/*  Branding Tab                                              */
/* ═══════════════════════════════════════════════════════════ */
const BrandingTab = ({ slug, company }: { slug: string; company?: ICompanyFull }) => {
  const queryClient = useQueryClient();
  const [primary, setPrimary] = React.useState(company?.primaryColor ?? '#0ea5c9');
  const [secondary, setSecondary] = React.useState(company?.secondaryColor ?? '#1D1D1E');
  const [accent, setAccent] = React.useState(company?.accentColor ?? '#5DE1FF');
  const [logoUrl, setLogoUrl] = React.useState(company?.logoUrl ?? '');

  React.useEffect(() => {
    if (company) {
      setPrimary(company.primaryColor ?? '#0ea5c9');
      setSecondary(company.secondaryColor ?? '#1D1D1E');
      setAccent(company.accentColor ?? '#5DE1FF');
      setLogoUrl(company.logoUrl ?? '');
    }
  }, [company]);

  const mutation = useMutation({
    mutationFn: () => companyService.updateBranding(slug, {
      primaryColor: primary,
      secondaryColor: secondary,
      accentColor: accent,
      logoUrl: logoUrl || undefined,
    }),
    onSuccess: () => { toast.success('Branding updated'); queryClient.invalidateQueries({ queryKey: ['company', slug] }); },
    onError: () => toast.error('Failed to update branding'),
  });

  const ColorField = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
    <div className="flex items-center gap-[12px]">
      <label className="w-[120px] text-[13px] font-medium text-[#818185] dark:text-[#A0A0A3]">{label}</label>
      <div className="flex items-center gap-[8px]">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-[36px] h-[36px] rounded-[8px] border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.12)] cursor-pointer" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-[100px] h-[36px] rounded-[10px] border border-[#BFBFC0] dark:border-[rgba(255,255,255,0.12)] dark:bg-[#28282B] dark:text-white px-[10px] text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-[rgba(93,225,255,0.2)]"
        />
      </div>
    </div>
  );

  return (
    <div>
      <h2 className="font-body text-[16px] font-semibold text-base-black dark:text-white mb-[20px]">Branding</h2>

      <div className="space-y-[16px] mb-[24px]">
        <div className="flex items-center gap-[12px]">
          <label className="w-[120px] text-[13px] font-medium text-[#818185] dark:text-[#A0A0A3]">Logo URL</label>
          <input
            type="text"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://..."
            className="flex-1 h-[36px] rounded-[10px] border border-[#BFBFC0] dark:border-[rgba(255,255,255,0.12)] dark:bg-[#28282B] dark:text-white dark:placeholder-[#6B6B6F] px-[10px] text-[13px] focus:outline-none focus:ring-2 focus:ring-[rgba(93,225,255,0.2)]"
          />
        </div>
        <ColorField label="Primary" value={primary} onChange={setPrimary} />
        <ColorField label="Secondary" value={secondary} onChange={setSecondary} />
        <ColorField label="Accent" value={accent} onChange={setAccent} />
      </div>

      {/* Preview */}
      <div className="rounded-[12px] border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.06)] p-[16px] mb-[20px]" style={{ background: secondary }}>
        <div className="flex items-center gap-[8px] mb-[12px]">
          {logoUrl && <img src={logoUrl} alt="" className="w-[24px] h-[24px] rounded-[4px] object-cover" />}
          <span className="text-[14px] font-semibold" style={{ color: accent }}>{company?.name ?? 'Company'}</span>
        </div>
        <div className="h-[6px] rounded-full" style={{ background: primary, width: '60%' }} />
      </div>

      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="h-[44px] px-[24px] bg-[#1D1D1E] dark:bg-[#28282B] text-white rounded-[32px] text-[14px] font-medium hover:bg-[#29292A] dark:hover:bg-[#333336] transition-all disabled:opacity-50"
      >
        {mutation.isPending ? 'Saving...' : 'Save Branding'}
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
/*  Templates Tab                                             */
/* ═══════════════════════════════════════════════════════════ */
const TemplatesTab = ({ slug, templates, isAdmin }: { slug: string; templates: IQueueTemplate[]; isAdmin: boolean }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = React.useState(false);
  const [name, setName] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [meetingUrl, setMeetingUrl] = React.useState('');

  const createMutation = useMutation({
    mutationFn: () => companyService.createTemplate(slug, { name, title, meetingUrl: meetingUrl || undefined }),
    onSuccess: () => {
      toast.success('Template created');
      queryClient.invalidateQueries({ queryKey: ['company-templates', slug] });
      setShowForm(false);
      setName('');
      setTitle('');
      setMeetingUrl('');
    },
    onError: () => toast.error('Failed to create template'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => companyService.deleteTemplate(slug, id),
    onSuccess: () => { toast.success('Template deleted'); queryClient.invalidateQueries({ queryKey: ['company-templates', slug] }); },
    onError: () => toast.error('Failed to delete template'),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-[16px]">
        <h2 className="font-body text-[16px] font-semibold text-base-black dark:text-white">Queue Templates</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="h-[36px] px-[16px] bg-[#1D1D1E] dark:bg-[#28282B] text-white rounded-[32px] text-[13px] font-medium hover:bg-[#29292A] dark:hover:bg-[#333336] transition-all"
        >
          {showForm ? 'Cancel' : '+ New Template'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }} className="space-y-[12px] mb-[20px] p-[16px] rounded-[12px] border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.06)] bg-[#F8FCFF] dark:bg-[#28282B]">
          <input
            type="text" placeholder="Template name" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full h-[44px] rounded-[24px] border border-[#BFBFC0] dark:border-[rgba(255,255,255,0.12)] dark:bg-[#1C1C1F] dark:text-white dark:placeholder-[#6B6B6F] px-[16px] text-[14px] focus:outline-none focus:ring-2 focus:ring-[rgba(93,225,255,0.2)] dark:focus:border-[#5DE1FF]/40 dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)]"
          />
          <input
            type="text" placeholder="Queue title" value={title} onChange={(e) => setTitle(e.target.value)}
            className="w-full h-[44px] rounded-[24px] border border-[#BFBFC0] dark:border-[rgba(255,255,255,0.12)] dark:bg-[#1C1C1F] dark:text-white dark:placeholder-[#6B6B6F] px-[16px] text-[14px] focus:outline-none focus:ring-2 focus:ring-[rgba(93,225,255,0.2)] dark:focus:border-[#5DE1FF]/40 dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)]"
          />
          <input
            type="text" placeholder="Meeting URL (optional)" value={meetingUrl} onChange={(e) => setMeetingUrl(e.target.value)}
            className="w-full h-[44px] rounded-[24px] border border-[#BFBFC0] dark:border-[rgba(255,255,255,0.12)] dark:bg-[#1C1C1F] dark:text-white dark:placeholder-[#6B6B6F] px-[16px] text-[14px] focus:outline-none focus:ring-2 focus:ring-[rgba(93,225,255,0.2)] dark:focus:border-[#5DE1FF]/40 dark:focus:shadow-[0_0_0_4px_rgba(93,225,255,0.12)]"
          />
          <button
            type="submit" disabled={!name.trim() || !title.trim() || createMutation.isPending}
            className="h-[44px] px-[24px] bg-[#1D1D1E] dark:bg-[#333336] text-white rounded-[32px] text-[14px] font-medium hover:bg-[#29292A] dark:hover:bg-[#3D3D40] transition-all disabled:opacity-50"
          >
            Create Template
          </button>
        </form>
      )}

      <div className="space-y-[8px]">
        {templates.map((t) => (
          <div key={t.id} className="flex items-center gap-[12px] p-[12px] rounded-[12px] border border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.06)]">
            <div className="flex-1 min-w-0">
              <p className="font-body text-[14px] font-medium text-base-black dark:text-white">{t.name}</p>
              <p className="text-[12px] text-[#818185] dark:text-[#6B6B6F] truncate">{t.title}{t.meetingUrl ? ` · ${t.meetingUrl}` : ''}</p>
            </div>
            {isAdmin && (
              <button
                onClick={() => deleteMutation.mutate(t.id)}
                className="text-[11px] px-[6px] py-[2px] rounded-[6px] border border-red-200 dark:border-red-500/30 text-[#D8210C] hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors shrink-0"
              >
                Delete
              </button>
            )}
          </div>
        ))}
        {templates.length === 0 && (
          <p className="text-center text-[14px] text-[#818185] dark:text-[#A0A0A3] py-[24px]">No templates yet</p>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
/*  Feature Toggles Tab                                       */
/* ═══════════════════════════════════════════════════════════ */
const TOGGLE_LABELS: Record<string, string> = {
  chat: 'Queue Chat',
  qrCheckin: 'QR Check-in',
  notifications: 'Push Notifications',
  waitEstimates: 'Wait Time Estimates',
  participantRename: 'Participant Rename',
  queuePasswords: 'Queue Passwords',
};

const DEFAULT_TOGGLES: Record<string, boolean> = {
  chat: true,
  qrCheckin: true,
  notifications: true,
  waitEstimates: true,
  participantRename: true,
  queuePasswords: true,
};

/* ═══════════════════════════════════════════════════════════ */
/*  My QR Tab                                                  */
/* ═══════════════════════════════════════════════════════════ */
const MyQrTab = ({ members, userId }: { members: ICompanyMember[]; userId?: number }) => {
  const qrCanvasRef = React.useRef<any>(null);
  const [fullscreen, setFullscreen] = React.useState(false);

  const myMember = members.find((m) => m.userId === userId);
  const qrToken = myMember?.qrToken;
  const qrUrl = qrToken ? `${window.location.origin}/qr/${qrToken}` : '';

  const handleDownload = () => {
    const canvas = qrCanvasRef.current?.querySelector?.('canvas') as HTMLCanvasElement | null;
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = 'my-qr-code.png';
    a.click();
  };

  if (!qrToken) {
    return (
      <div className="text-center py-[40px]">
        <p className="font-body text-[14px] text-[#818185] dark:text-[#A0A0A3]">
          No QR token found for your account.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="font-body text-[16px] font-semibold text-base-black dark:text-white mb-[4px]">My QR Code</h2>
      <p className="text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[20px]">
        Show this QR code to a queue admin to quickly check in.
      </p>

      <div className="flex flex-col items-center gap-[20px]">
        {/* QR code display */}
        <div ref={qrCanvasRef} className="bg-white rounded-[12px] p-[16px] border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.06)]">
          <QRCodeCanvas value={qrUrl} size={200} level="M" />
        </div>

        {/* URL text */}
        <p className="font-mono text-[12px] text-[#818185] dark:text-[#6B6B6F] text-center break-all max-w-[280px]">
          {qrUrl}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-[8px]">
          <button
            onClick={handleDownload}
            className="h-[44px] px-[20px] inline-flex items-center gap-[6px] bg-[#EFEFF1] dark:bg-[#28282B] text-base-black dark:text-white rounded-[32px] text-[13px] font-medium hover:bg-[#E5E5ED] dark:hover:bg-[#333336] transition-all border border-[#E5E5ED] dark:border-[rgba(255,255,255,0.08)]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Download
          </button>
          <button
            onClick={() => setFullscreen(true)}
            className="h-[44px] px-[20px] inline-flex items-center gap-[6px] bg-[#1D1D1E] dark:bg-[#28282B] text-white rounded-[32px] text-[13px] font-medium hover:bg-[#29292A] dark:hover:bg-[#333336] transition-all"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            Present
          </button>
        </div>
      </div>

      {/* Fullscreen overlay */}
      {fullscreen && (
        <div className="fixed inset-0 z-[200] bg-black flex items-center justify-center" onClick={() => setFullscreen(false)}>
          <button
            onClick={() => setFullscreen(false)}
            className="absolute top-[20px] right-[20px] w-[40px] h-[40px] rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <div className="flex flex-col items-center gap-[24px]" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white rounded-[16px] p-[24px]">
              <QRCodeSVG value={qrUrl} size={340} level="M" />
            </div>
            <p className="font-mono text-[13px] text-white/70 text-center break-all max-w-[380px]">
              {qrUrl}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const FeatureTogglesTab = ({ slug, company }: { slug: string; company?: ICompanyFull }) => {
  const queryClient = useQueryClient();
  const toggles = { ...DEFAULT_TOGGLES, ...((company?.featureToggles as Record<string, boolean>) ?? {}) };

  const mutation = useMutation({
    mutationFn: (update: Record<string, boolean>) => companyService.updateFeatureToggles(slug, update),
    onSuccess: () => { toast.success('Feature toggle updated'); queryClient.invalidateQueries({ queryKey: ['company', slug] }); },
    onError: () => toast.error('Failed to update toggle'),
  });

  return (
    <div>
      <h2 className="font-body text-[16px] font-semibold text-base-black dark:text-white mb-[4px]">Feature Toggles</h2>
      <p className="text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[20px]">Enable or disable features for all queues in your company.</p>

      <div className="space-y-[12px]">
        {Object.entries(TOGGLE_LABELS).map(([key, label]) => (
          <div key={key} className="flex items-center justify-between p-[12px] rounded-[12px] border border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.06)]">
            <span className="font-body text-[14px] text-base-black dark:text-white">{label}</span>
            <button
              onClick={() => mutation.mutate({ [key]: !toggles[key] })}
              disabled={mutation.isPending}
              className={`relative w-[44px] h-[24px] rounded-full transition-colors ${
                toggles[key] ? 'bg-[#0ea5c9]' : 'bg-[#BFBFC0] dark:bg-[#3D3D40]'
              }`}
            >
              <span className={`absolute top-[2px] w-[20px] h-[20px] rounded-full bg-white shadow transition-transform ${
                toggles[key] ? 'left-[22px]' : 'left-[2px]'
              }`} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
